import React, { useRef, useEffect } from 'react'

interface ProgressCircleProps {
  percentage: number
  size?: number
  showPercentage?: boolean
  isAnimating?: boolean
  animationStartedTimestamp?: number
}

const ANIMATION_DURATION_MS = 3 * 60000

export const ProgressCircle: React.FC<ProgressCircleProps> = ({
  percentage,
  size = 48,
  showPercentage = true,
  isAnimating = false,
  animationStartedTimestamp = undefined,
}) => {
  const progressColor = percentage === 100 ? 'var(--zocks-success)' : 'var(--zocks-primary)'
  const circleRef = useRef<SVGCircleElement>(null)
  const animationRef = useRef<Animation | null>(null)

  const radius = 20
  const strokeWidth = 4
  const circumference = 2 * Math.PI * radius
  const center = 24

  useEffect(() => {
    if (animationStartedTimestamp === undefined) {
      return
    }

    const circle = circleRef.current
    if (!circle) {
      return
    }

    if (animationRef.current) {
      animationRef.current.cancel()
      animationRef.current = null
    }

    if (isAnimating) {
      animationRef.current = circle.animate([{ strokeDasharray: `0 ${circumference}` }, { strokeDasharray: `${circumference} ${circumference}` }], {
        duration: ANIMATION_DURATION_MS,
        easing: 'linear',
        fill: 'forwards',
      })
    }

    return () => {
      if (animationRef.current) {
        animationRef.current.cancel()
      }
    }
  }, [isAnimating, animationStartedTimestamp, circumference])

  const stateClass = percentage === 100 ? 'progress-circle-completed' : ''
  const staticDasharray = isAnimating ? `0 ${circumference}` : `${(percentage / 100) * circumference} ${circumference}`

  return (
    <div className={`position-relative d-flex align-items-center justify-content-center ${stateClass}`} style={{ width: `${size}px`, height: `${size}px` }}>
      <svg width={size} height={size} viewBox={`0 0 48 48`} className='position-absolute top-0 start-0'>
        <circle cx={center} cy={center} r={radius} fill='none' stroke='var(--zocks-gray-200)' strokeWidth={strokeWidth} />
        <circle
          ref={circleRef}
          cx={center}
          cy={center}
          r={radius}
          fill='none'
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeDasharray={staticDasharray}
          strokeLinecap='round'
          transform={`rotate(-90 ${center} ${center})`}
          className='progress-stroke'
        />
      </svg>
      {showPercentage ? <span className='fw-bold zocks-text-gray-700 text-center position-relative paragraph-extra-small z-1'>{percentage}%</span> : null}
    </div>
  )
}
